import { DEFAULT_LANGUAGE, languageById, translate } from "../shared/i18n";

// t("english text") everywhere in the renderer. the page's own words are swapped once at boot by translatePage,
// so new html only needs plain english and a line in each catalogue

const STORE_KEY = "chi-language";
const ATTRIBUTES = ["placeholder", "title", "aria-label"];

let current = readStored();
// the page language is set from the first paint, not only after the settings load
if (typeof document !== "undefined") document.documentElement.lang = languageById(current).locale;
// what each node said in english, so switching language again starts from the source and not from the last swap
const originalText = new WeakMap<Text, string>();
const originalAttrs = new WeakMap<Element, Record<string, string>>();

function readStored(): string {
  try {
    return languageById(localStorage.getItem(STORE_KEY) || DEFAULT_LANGUAGE).id;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

export function t(text: string, vars?: Record<string, string | number>): string {
  return translate(current, text, vars);
}

export function currentLanguage(): string {
  return current;
}

// for toLocaleString and Intl, follows the language picked in setup
export function locale(): string {
  return languageById(current).locale;
}

export function setLanguage(id: string | undefined): boolean {
  const next = languageById(id).id;
  const changed = next !== current;
  current = next;
  document.documentElement.lang = languageById(next).locale;
  try {
    localStorage.setItem(STORE_KEY, next);
  } catch {
    // private windows can refuse storage, the setting still comes from the server next time
  }
  return changed;
}

function swapText(node: Text): void {
  const source = originalText.get(node) ?? node.data;
  const key = source.replace(/\s+/g, " ").trim();
  if (!key || !/[a-z]/i.test(key)) return;
  originalText.set(node, source);
  const lead = source.match(/^\s*/)?.[0] ?? "";
  const tail = source.match(/\s*$/)?.[0] ?? "";
  node.data = lead + t(key) + tail;
}

function swapAttrs(element: Element): void {
  const saved = originalAttrs.get(element) ?? {};
  // a field with only a placeholder still gets a name for screen readers
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    const unnamed = !element.hasAttribute("aria-label") && !element.hasAttribute("aria-labelledby") && !element.labels?.length && element.type !== "hidden";
    if (unnamed && element.placeholder.trim()) element.setAttribute("aria-label", element.placeholder.trim());
  }
  for (const name of ATTRIBUTES) {
    const source = saved[name] ?? element.getAttribute(name);
    if (!source) continue;
    saved[name] = source;
    element.setAttribute(name, t(source.trim()));
  }
  originalAttrs.set(element, saved);
}

export function translateTree(root: Node): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => (node instanceof Element ? node : node.parentElement)?.closest("script, style, [translate='no']") ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
  });
  for (let node = walker.currentNode as Node | null; node; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) swapText(node as Text);
    else if (node instanceof Element) {
      swapAttrs(node);
      if (node instanceof HTMLTemplateElement) translateTree(node.content);
    }
  }
}

export function translatePage(): void {
  translateTree(document.body);
}
